import {
  useLoaderData,
  useRouteLoaderData,
  useParams,
  useNavigate,
  useRevalidator,
  useSearchParams,
  Outlet,
} from 'react-router-dom'
import { useCallback, useMemo, useState } from 'react'
import Stack from '@mui/material/Stack'
import IconButton from '@mui/material/IconButton'
import Button from '@mui/material/Button'
import Tooltip from '@mui/material/Tooltip'
import Divider from '@mui/material/Divider'
import Typography from '@mui/material/Typography'
import LaunchIcon from '@mui/icons-material/Launch'
import DeleteIcon from '@mui/icons-material/Delete'
import VisibilityIcon from '@mui/icons-material/Visibility'
import EmailIcon from '@mui/icons-material/Email'
import AddBoxIcon from '@mui/icons-material/AddBox'
import EditIcon from '@mui/icons-material/Edit'
import { GridColDef, GridInitialState, DataGridPremiumProps } from '@mui/x-data-grid-premium'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import upperFirst from 'lodash/upperFirst'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'

import { LoaderData, OptionsParticipants, OptionType, SubEventType } from '../../../types'
import {
  useMembershipLevelOptions,
  useJobTitlesDataOptions,
  useLocationDataOptions,
  useParticipantOptionStatusOptions,
  formatDateTime,
  useParticipantStatusOptions,
} from '../../../utils'
import {
  useDeleteOptionParticipationMutation,
  useLazyGetParticipantQuery,
} from '../../../services/events'
import { adminOptionsParticipantsLoader, adminEventDetailsLoader } from '../Admin.loader'
import StripedDataGrid from '../../root/components/StripedDataGrid'
import MasterDetailsContent from '../../root/components/MasterDetailsContent'
import IconButtonOutlined from '../../root/components/IconButtonOutlined'
import AdminOptionDetails from './components/AdminOptionDetails'
import ParticipantOptionStatusTag from '../../events/components/ParticipantOptionStatusTag'
import { useContextShowStatistics } from '../Admin.Context'
import BulkEditOptionsParticipants from './components/BulkEditOptionsParticipants'
import ViewButtonGroup from './components/ViewButtonGroup'
import AdminParticipantDetails from './AdminParticipantDetails'
import BooleanRender from './components/BooleanRender'
import { HEADER_HEIGHT } from '../../root/components/Header'
import { SUB_HEADER_HEIGHT } from '../../root/components/SubHeader'

type Row = OptionsParticipants<true>

function MasterPanelParticipantDetails({ row }: { row: Row }) {
  const { eventId } = useParams()
  const [getParticipant, participant] = useLazyGetParticipantQuery()
  const participantId = row.participant_id

  const callback = useCallback(async () => {
    await getParticipant({ eventId: Number(eventId), id: participantId })
  }, [eventId, participantId, getParticipant])

  return (
    <MasterDetailsContent callback={callback}>
      <AdminParticipantDetails participant={participant?.data} readOnly />
    </MasterDetailsContent>
  )
}

const getDetailPanelHeight: DataGridPremiumProps['getDetailPanelHeight'] = () => 'auto'
const getDetailPanelContent: DataGridPremiumProps['getDetailPanelContent'] = params => (
  <MasterPanelParticipantDetails row={params.row as Row} />
)

export default function AdminOptionParticipants() {
  const { t } = useTranslation('admin')
  const { eventId, optionId } = useParams()
  const navigate = useNavigate()
  const revalidator = useRevalidator()
  const [searchParams, setSearchParams] = useSearchParams()
  const [openOptions, setOpenOptions] = useState(false)
  const [currentParticipations, setCurrentParticipations] = useState<Map<number, Row>>(new Map())
  if (!eventId || Number.isNaN(eventId)) throw new Error('Event ID is required')
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  const showStatistics = useContextShowStatistics()

  const { event, subEvents, options } = useRouteLoaderData('adminEventDetails') as LoaderData<
    ReturnType<typeof adminEventDetailsLoader>
  >
  const { participants } = useLoaderData() as LoaderData<
    ReturnType<typeof adminOptionsParticipantsLoader>
  >
  const rows = participants?.results satisfies Row[] | undefined

  const [deleteOptionParticipation] = useDeleteOptionParticipationMutation()

  const onCancel = useCallback(
    (participantId: number) => {
      const text = t('Êtes-vous sûr de vouloir supprimer cette option ?')

      if (window.confirm(text)) {
        deleteOptionParticipation({ eventId: Number(eventId), id: participantId })
          .then(data => {
            revalidator.revalidate()
            if ('data' in data) {
              toast.info(t("Participation à l'option annulée"))
            }
          })
          .catch(() => {
            toast.error(t('Une erreur est survenue'))
          })
      }
    },
    [deleteOptionParticipation, t, eventId, revalidator]
  )
  const onEdit = useCallback(
    (id: number) => {
      navigate(
        `/admin/details/${eventId}/options/${optionId}/details/${id}?${searchParams.toString()}`
      )
    },
    [optionId, navigate, eventId, searchParams]
  )

  const membershipLevelOptions = useMembershipLevelOptions(t)
  const locationDataOptions = useLocationDataOptions()
  const jobTitlesDataOptions = useJobTitlesDataOptions()
  const participantOptionStatusOptions = useParticipantOptionStatusOptions(t)
  const participantStatusOptions = useParticipantStatusOptions(t)

  const optionDetails = useMemo(() => {
    if (Number.isNaN(optionId)) return undefined
    return options?.find(el => el.id === Number(optionId))
  }, [optionId, options])

  const subEventDetails = useMemo(() => {
    if (optionDetails?.type !== OptionType.SubEventOption) return undefined
    return subEvents?.find(el => el.id === optionDetails.sub_event)
  }, [optionDetails, subEvents])

  const columns: GridColDef<Row>[] = useMemo(
    () => [
      {
        field: 'actions',
        headerName: t('Actions'),
        type: 'actions',
        width: 180,
        disableExport: true,
        filterable: false,
        renderCell: params => {
          const row = params.row
          const participantId = row.participant_id
          return (
            <Stack direction="row">
              <Tooltip title={t('Voir détails du participant')}>
                <IconButton
                  color="primary"
                  onClick={e => {
                    e.preventDefault()
                    e.stopPropagation()
                    navigate(`/admin/details/${eventId}/participants/${participantId}`)
                  }}
                >
                  <VisibilityIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title={t('Modifier inscription complémentaire')}>
                <IconButton
                  onClick={e => {
                    e.preventDefault()
                    e.stopPropagation()
                    onEdit(row.id)
                  }}
                >
                  <EditIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title={t('Envoyer un email')}>
                <IconButton
                  onClick={e => {
                    e.preventDefault()
                    e.stopPropagation()
                    const subject = `SFCTCV Événement ${event?.name} - Information sur votre réservation`
                    const body = `Bonjour ${row.first_name} ${row.last_name},%0D%0A%0D%0ANous venons de mettre à jour votre réservation pour l'événement ${event?.name}.%0D%0A%0D%0ACordialement,%0D%0AL'équipe SFCTCV`

                    window.open(`mailto:${row.e_mail}?subject=${subject}&body=${body}"`, '_blank')
                  }}
                >
                  <EmailIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title={t("Annuler inscription à l'option")}>
                <IconButton
                  color="error"
                  onClick={e => {
                    e.preventDefault()
                    e.stopPropagation()
                    onCancel(row.id)
                  }}
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          )
        },
      },
      { field: 'id', headerName: t('ID'), width: 60 },
      {
        field: 'created',
        headerName: t('Date d’inscription'),
        width: 150,
        type: 'dateTime',
        valueGetter: value => value && new Date(value),
        valueFormatter: value => (value ? formatDateTime(value) : 'NC'),
      },
      {
        field: 'status',
        headerName: t("Statut d'inscription"),
        width: 210,
        display: 'flex',
        type: 'singleSelect',
        valueOptions:
          optionId !== undefined ? participantOptionStatusOptions : participantStatusOptions,
        renderCell: params => (
          <ParticipantOptionStatusTag size="small" status={params.row.status} />
        ),
      },
      {
        field: 'last_name',
        headerName: t('Nom'),
        width: 150,
      },
      {
        field: 'first_name',
        headerName: t('Prénom'),
        width: 150,
      },
      {
        field: 'hotel',
        headerName: t('Hôtel'),
        width: 300,
        type: 'singleSelect',
        valueOptions: locationDataOptions,
        valueFormatter: value =>
          value
            ? (locationDataOptions.find(option => option.value === value)?.label ?? 'NC')
            : 'N/A',
      },
      {
        field: 'present',
        headerName: t('Présent'),
        width: 100,
        type: 'boolean',
        renderCell: BooleanRender,
      },
      {
        field: 'price',
        headerName: t('Paiement'),
        type: 'number',
        width: 100,
      },
      {
        field: 'membership_level',
        headerName: t('Statut du membre'),
        type: 'singleSelect',
        valueOptions: membershipLevelOptions,
        width: 150,
        valueFormatter: value =>
          membershipLevelOptions.find(option => option.value === value)?.label ?? 'NC',
      },
      {
        field: 'home_region',
        headerName: t('Région'),
        width: 100,
        valueFormatter: value => value ?? 'NC',
      },
      {
        field: 'service_name',
        headerName: t('Service'),
        width: 300,
      },
      {
        field: 'job_title',
        headerName: t('Type de poste'),
        type: 'singleSelect',
        valueOptions: jobTitlesDataOptions,
        width: 250,
        valueFormatter: value =>
          upperFirst(jobTitlesDataOptions.find(option => option.value === value)?.label ?? 'NC'),
      },
      {
        field: 'training_phase',
        headerName: t('Phase de formation'),
        width: 200,
        valueFormatter: value => value ?? 'NC',
      },
      {
        field: 'phone_number',
        headerName: t('Téléphone'),
        width: 170,
      },
      {
        field: 'e_mail',
        headerName: t('Email'),
        width: 250,
      },
      {
        field: 'civi_link',
        headerName: t('Lien CiviCRM'),
        width: 150,
        filterable: false,
        disableExport: true,
        renderCell: params => (
          <Button
            variant="outlined"
            color="primary"
            href={params.value as Row['civi_link']}
            target="_blank"
            rel="noopener noreferrer"
            endIcon={<LaunchIcon />}
          >
            {t('Ouvrir')}
          </Button>
        ),
      },
    ],
    [
      t,
      optionId,
      participantOptionStatusOptions,
      participantStatusOptions,
      locationDataOptions,
      membershipLevelOptions,
      jobTitlesDataOptions,
      navigate,
      eventId,
      onEdit,
      event?.name,
      onCancel,
    ]
  )

  const initialState: GridInitialState = useMemo(
    () => ({
      columns: {
        columnVisibilityModel: {
          id: false,
          hotel:
            optionDetails?.type === OptionType.SubEventOption &&
            subEventDetails?.sub_event_type === SubEventType.HotelSubEvent,
        },
      },
    }),
    [optionDetails?.type, subEventDetails?.sub_event_type]
  )

  const ResponsiveButton = isMobile ? IconButtonOutlined : Button

  return (
    <>
      <Stack
        sx={theme => ({
          backgroundColor: theme.palette.background.paper,
          borderRadius: '0.5rem',
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: theme.boxShadows.main,
        })}
        padding="0.5rem"
        height="inherit"
        overflow="auto"
      >
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="h2">
            {t('Détails option')} : {optionDetails?.name ?? 'NC'}
          </Typography>
          <Typography variant="h2">{event?.name}</Typography>
        </Stack>

        {showStatistics && (
          <AdminOptionDetails option={optionDetails} participantsCount={participants?.count} />
        )}

        <Divider />

        <Stack>
          <Stack
            direction={['column', 'row']}
            alignItems={['start', 'center']}
            justifyContent="space-between"
            sx={{ margin: '1rem 0' }}
            gap="0.5rem"
          >
            <Stack direction="row" alignItems="center" gap="1rem">
              <h3>{t('Liste des participants')} :</h3>
              <ResponsiveButton
                startIcon={<AddBoxIcon />}
                variant="contained"
                color="primary"
                onClick={() => {
                  navigate(
                    `/admin/details/${eventId}/options/${optionId}/details/new?${searchParams.toString()}`
                  )
                }}
              >
                {isMobile ? <AddBoxIcon /> : t('Option')}
              </ResponsiveButton>
            </Stack>
            <ViewButtonGroup />
          </Stack>
          <Stack
            sx={{
              maxHeight: `calc(100vh - ${HEADER_HEIGHT} - ${SUB_HEADER_HEIGHT} - 40px - 4.5rem${!showStatistics ? ' - 60px' : ''})`,
              overflow: 'auto',
            }}
          >
            <StripedDataGrid
              rows={rows}
              columns={columns}
              count={participants?.count}
              // Server side filter with searchParams
              onFilterModelChange={model => {
                const status = model.items.find(item => item.field === 'status')
                if (status?.value && status.operator === 'is') {
                  searchParams.set('status', status.value as string)
                  setSearchParams(searchParams, { replace: true })
                } else {
                  searchParams.delete('status')
                  setSearchParams(searchParams, { replace: true })
                }
              }}
              getDetailPanelHeight={getDetailPanelHeight}
              getDetailPanelContent={getDetailPanelContent}
              initialState={initialState}
              emailOptions={{
                getValue: row => (row as Row).e_mail,
                body: `Bonjour,%0D%0A%0D%0ANous venons de mettre à jour votre réservation pour l'événement ${event?.name}.%0D%0A%0D%0ACordialement,%0D%0AL'équipe SFCTCV`,
                subject: `SFCTCV Événement ${event?.name} - Information sur votre réservation`,
              }}
              editOptions={{
                onClick: selectedRows => {
                  setOpenOptions(true)
                  setCurrentParticipations(selectedRows as Map<number, Row>)
                },
              }}
              sx={{
                '& .MuiDataGrid-detailPanel': {
                  overflow: 'visible',
                },
              }}
            />
          </Stack>
        </Stack>
      </Stack>

      <BulkEditOptionsParticipants
        open={openOptions}
        setOpen={setOpenOptions}
        currentParticipations={currentParticipations}
        name={optionDetails?.name}
        optionType={optionDetails?.type}
        subEventType={subEventDetails?.sub_event_type}
      />

      <Outlet />
    </>
  )
}
